import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtUsd, fmtNum } from '../lib/format.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ReportsSkeleton } from './ReportsSkeleton.jsx';
import { YoyCell } from './YoyCell.jsx';
import { TrendLineChart } from './TrendLineChart.jsx';

export function Step1View({ location, user }) {
  const isManager = user && window.EmpireRoles ? window.EmpireRoles.isManager(user) : false;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const locationKey = locationKeyFromName(location?.name);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!locationKey) return;
    try {
      const url = `/api/weekly/step1/${locationKey}${force ? '?force=1' : ''}`;
      const res = await api('GET', url);
      setData(res); setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load year context');
    } finally {
      setLoading(false);
    }
  }, [locationKey]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (!locationKey) return <div className="card text-sm text-gray-500">Pick a location.</div>;
  if (loading && !data) return <ReportsSkeleton label="Loading year context…" />;
  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-sm font-semibold text-red-700 mb-1">Year context unavailable</div>
    <div className="text-xs text-gray-600">{error}</div>
    <button className="btn-secondary mt-3" onClick={load}>Retry</button>
  </div>;
  if (!data) return null;

  if (!data.available) {
    const reason = data.reason === 'unconfigured'
      ? 'Set PIS_API_KEY in .env to pull historical sales from pis.empireteaandcoffee.com.'
      : `pis backend error: ${data.error || data.reason}`;
    return (
      <div className="card" style={{ borderLeft: '4px solid #f9a825', background: '#fff8e1' }}>
        <div className="text-sm font-semibold" style={{ color: '#a16207' }}>Historical sales not yet wired</div>
        <div className="text-xs" style={{ color: '#5d4037', marginTop: 4 }}>{reason}</div>
        <button className="btn-secondary mt-3" onClick={load}>Retry</button>
      </div>
    );
  }

  const { meta, step1 } = data;
  const ctx = step1.contextSummary || {};
  const trends = step1.weeklyTrends || [];
  const isThin = !!meta.thinData;

  return (
    <div className="space-y-3">
      {/* Section heading mirrors PIS Step 1 — explicit step label + location/week subtitle */}
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#212121' }}>Step 1: Where Are We in the Year</div>
        <div style={{ fontSize: 13, color: '#757575', marginTop: 2 }}>
          {meta.locationName} — {ctx.isoWeekStr || ''}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}>ISO Week</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#212121', marginTop: 4 }}>{ctx.isoWeekStr || '—'}</div>
          <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>{ctx.dateRange || ''}</div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}>This week so far</div>
          {ctx.thisWeekTransactions > 0 ? (
            <>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#212121', marginTop: 4 }}>{fmtNum(ctx.thisWeekTransactions)} tx</div>
              {isManager && <div style={{ fontSize: 13, color: '#757575' }}>{fmtUsd(ctx.thisWeekSales)}</div>}
            </>
          ) : (
            <div style={{ fontSize: 16, color: '#757575', marginTop: 6 }}>Week in Progress</div>
          )}
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}>Same week LY</div>
          {(!isThin && ctx.sameWeekLastYear != null) ? (
            <div style={{ fontSize: 22, fontWeight: 700, color: '#212121', marginTop: 4 }}>{fmtNum(ctx.sameWeekLastYear)} tx</div>
          ) : (
            <div style={{ fontSize: 16, color: '#757575', marginTop: 6 }}>{isThin ? 'Thin data' : 'No data'}</div>
          )}
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}
               title="This week's transactions compared to the same ISO week last year.">
            This week vs same week LY
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
            <YoyCell pct={ctx.yoyPct} isThin={isThin} />
          </div>
          <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 2 }}>Δ tx, this week vs last year's same week</div>
        </div>
      </div>

      {ctx.dateRange && (
        <div className="card" style={{ background: '#e3f2fd', borderLeft: '4px solid #1565c0' }}>
          <div style={{ fontSize: 13, color: '#0d47a1' }}>
            <strong>{ctx.isoWeekStr || ''}:</strong> {ctx.dateRange}
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#212121' }}>
            Weekly Transaction Trend — {meta.locationName}
          </div>
          {trends.length > 0 && trends.length < 26 && (
            <div style={{ fontSize: 11, color: '#a16207', background: '#fff8e1', padding: '3px 8px', borderRadius: 10 }}
                 title="The pis.empireteaandcoffee.com /api/wizard/data endpoint currently returns 8 weeks of history. PIS Step 1 itself shows ~52 weeks; ask CJ to extend the wizard's weeklyTrends cap.">
              Showing {trends.length} weeks (API-limited)
            </div>
          )}
        </div>
        <TrendLineChart rows={trends} locationName={meta.locationName}
          annualAverageOverride={ctx.annualAverage ?? ctx.ytdWeeklyAverage ?? null} />
      </div>

      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 10 }}>Recent weeks</div>
        {trends.length === 0 ? (
          <div style={{ fontSize: 13, color: '#757575' }}>No weekly rows.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#757575' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Date range</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Transactions</th>
                  {isManager && <th style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Net sales</th>}
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>YoY</th>
                </tr>
              </thead>
              <tbody>
                {/* PIS payload is newest-first; the Recent Weeks table shows the
                    8 most-recent completed weeks. Don't reverse before slicing —
                    that's the chart's job (chart wants oldest-first). */}
                {trends.slice(0, 8).map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{r.date_range || `W${r.iso_week}`}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{fmtNum(r.transactions)}</td>
                    {isManager && <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{fmtUsd(r.net_sales)}</td>}
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>
                      <YoyCell pct={r.yoy_pct} isThin={isThin} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
